/**
 * Notification types and navigation targets for the Admin Notification Centre.
 *
 * Notifications are written by the database at the moment the underlying record
 * changes (see the `notify_company_admins` writer and its triggers), so the same
 * operational event produces exactly one row per admin recipient regardless of
 * which client, edge function or background job caused it.
 *
 * This module is the single place that knows what a type means on the client:
 * the string constants, the icon shown in the bell, and where clicking the
 * notification should take the admin. Nothing here fetches or writes data.
 */

export const NOTIFICATION_TYPES = {
  QUOTE_ACCEPTED: "quote_accepted",
  QUOTE_DECLINED: "quote_declined",
  JOB_CREATED: "job_created",
  DRIVER_ASSIGNED: "driver_assigned",
  VEHICLE_COLLECTED: "vehicle_collected",
  VEHICLE_DELIVERED: "vehicle_delivered",
  POC_COMPLETED: "poc_completed",
  POD_COMPLETED: "pod_completed",
  APPRAISAL_COMPLETED: "appraisal_completed",
  INVOICE_GENERATED: "invoice_generated",
} as const;

export type NotificationType =
  (typeof NOTIFICATION_TYPES)[keyof typeof NOTIFICATION_TYPES];

/** Every admin operational type, in the order they appear in the workflow. */
export const ADMIN_NOTIFICATION_TYPES: NotificationType[] = [
  NOTIFICATION_TYPES.QUOTE_ACCEPTED,
  NOTIFICATION_TYPES.QUOTE_DECLINED,
  NOTIFICATION_TYPES.JOB_CREATED,
  NOTIFICATION_TYPES.DRIVER_ASSIGNED,
  NOTIFICATION_TYPES.VEHICLE_COLLECTED,
  NOTIFICATION_TYPES.VEHICLE_DELIVERED,
  NOTIFICATION_TYPES.POC_COMPLETED,
  NOTIFICATION_TYPES.POD_COMPLETED,
  NOTIFICATION_TYPES.APPRAISAL_COMPLETED,
  NOTIFICATION_TYPES.INVOICE_GENERATED,
];

const ICONS: Record<string, string> = {
  [NOTIFICATION_TYPES.QUOTE_ACCEPTED]: "✅",
  [NOTIFICATION_TYPES.QUOTE_DECLINED]: "❌",
  [NOTIFICATION_TYPES.JOB_CREATED]: "🆕",
  [NOTIFICATION_TYPES.DRIVER_ASSIGNED]: "🧑‍✈️",
  [NOTIFICATION_TYPES.VEHICLE_COLLECTED]: "🚗",
  [NOTIFICATION_TYPES.VEHICLE_DELIVERED]: "📦",
  [NOTIFICATION_TYPES.POC_COMPLETED]: "✅",
  [NOTIFICATION_TYPES.POD_COMPLETED]: "📦",
  [NOTIFICATION_TYPES.APPRAISAL_COMPLETED]: "📝",
  [NOTIFICATION_TYPES.INVOICE_GENERATED]: "🧾",
  // Pre-existing types kept working unchanged.
  job_assigned: "🚗",
  collection_reminder: "⏰",
  job_updated: "📝",
  vehicle_reminder: "⚠️",
  tracking_shared: "📍",
  expense_uploaded: "🧾",
  automation: "⚙️",
  warning: "⚠️",
  error: "⛔",
};

export function getNotificationIcon(type: string | null | undefined): string {
  if (!type) return "📣";
  return ICONS[type] ?? "📣";
}

export interface NotificationLinkSource {
  type?: string | null;
  link?: string | null;
  entity_type?: string | null;
  entity_id?: string | null;
  data?: unknown;
}

function readId(data: unknown, key: string): string | null {
  if (!data || typeof data !== "object") return null;
  const value = (data as Record<string, unknown>)[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * Where a notification should navigate to, or null when there is nothing
 * meaningful to open. The stored `link` is authoritative; the fallbacks exist
 * so notifications written before links existed still navigate correctly.
 */
export function resolveNotificationLink(
  notification: NotificationLinkSource,
): string | null {
  const link = notification.link?.trim();
  if (link && link.startsWith("/")) return link;

  const data = notification.data;
  const appraisalId = readId(data, "appraisalId") ?? readId(data, "appraisal_id");
  const jobId = readId(data, "jobId") ?? readId(data, "job_id");
  const quoteId = readId(data, "quoteId") ?? readId(data, "quote_id");

  if (notification.entity_type === "appraisal" && notification.entity_id) {
    return `/appraisal/${notification.entity_id}`;
  }
  if (notification.entity_type === "job" && notification.entity_id) {
    return `/job/${notification.entity_id}`;
  }
  if (notification.entity_type === "quote" && notification.entity_id) {
    return `/saved-quotes?quote=${notification.entity_id}`;
  }

  if (quoteId) return `/saved-quotes?quote=${quoteId}`;
  if (appraisalId) return `/appraisal/${appraisalId}`;
  if (jobId) return `/job/${jobId}`;
  return null;
}
